import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';

interface MaterialCreateDialogProps {
  open: boolean;
  onClose: () => void;
  categoryId: string;
  onSuccess?: (material: any) => void;
}

export const MaterialCreateDialog: React.FC<MaterialCreateDialogProps> = ({
  open,
  onClose,
  categoryId,
  onSuccess,
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();

  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<any>({});

  const [form, setForm] = useState({
    name: '',
    unit: '',
    cost: 0,
    description: '',
    isActive: true,
  });

  useEffect(() => {
    if (open) {
      setForm({ name: '', unit: '', cost: 0, description: '', isActive: true });
      setErrors({});
    }
  }, [open]);

  const handleChange = (field: string, value: any) => {
    setForm(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validate = () => {
    const newErr: any = {};
    if (!form.name || !form.name.trim()) {newErr.name = 'Name is required';}
    if (isNaN(Number(form.cost))) {newErr.cost = 'Cost must be a number';}
    setErrors(newErr);
    return Object.keys(newErr).length === 0;
  };

  const handleCreate = async () => {
    if (!validate()) {return;}
    setSaving(true);
    try {
      const payload: any = {
        name: form.name,
        categoryId,
        unit: form.unit || undefined,
        cost: Number(form.cost),
        description: form.description || undefined,
        isActive: !!form.isActive,
      };

      // Use resource name 'materials' to match backend POST /materials
      const { data } = await dataProvider.create('materials', { data: payload });

      notify('Material created', { type: 'success' });
      refresh();
      if (onSuccess) {onSuccess(data);}
      onClose();
    } catch (error: any) {
      notify(error?.message || 'Failed to create material', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Material</DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative' }}>
          {saving && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                bgcolor: 'rgba(255,255,255,0.7)',
                zIndex: 10,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => handleChange('name', e.target.value)}
                fullWidth
                required
                error={!!errors.name}
                helperText={errors.name}
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                label="Unit"
                value={form.unit}
                onChange={(e) => handleChange('unit', e.target.value)}
                fullWidth
                disabled={saving}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Cost"
                value={form.cost}
                onChange={(e) => handleChange('cost', e.target.value)}
                fullWidth
                error={!!errors.cost}
                helperText={errors.cost}
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Description"
                value={form.description}
                onChange={(e) => handleChange('description', e.target.value)}
                fullWidth
                multiline
                rows={3}
                disabled={saving}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.isActive}
                    onChange={(e) => handleChange('isActive', e.target.checked)}
                    disabled={saving}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button onClick={handleCreate} variant="contained" disabled={saving}>
          {saving ? 'Creating...' : 'Create Material'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MaterialCreateDialog;
